import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { IApiPathManager } from '../config/ApiPathManager';
import { container } from '../../DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { IAddDeviceRequest, IAddDeviceResponse, IAxiosRetryConfig, IDeviceData } from '../../entity/interface/iDevices';
import { IDeviceRepository } from '../../core/repositories/interfaces/iDeviceRepository';
import axiosRetry from 'axios-retry';
import { API_IDS } from '@intigral/constant';

class DeviceRepository implements IDeviceRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async addDevice(
        username: string,
        apiKey: string,
        payload: IAddDeviceRequest,
        token: string,
        identityToken: string,
        axiosRetryConfig: IAxiosRetryConfig
    ): Promise<any> {
        const axiosRetryConfigIfExists: IAxiosRetryConfig =
            axiosRetryConfig != null ? axiosRetryConfig : { retries: 0 };
        const response = await this.http.post<any>(this.apiPathManager.getPath(API_IDS.DEVICE_ADD), {
            params: {
                apikey: apiKey,
                userName: username,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
            body: payload,
            'axios-retry': {
                ...axiosRetryConfigIfExists,
                retryDelay: axiosRetry.exponentialDelay,
                retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
            },
        });
        return response.data;
    }

    async deleteDevice(
        username: string,
        apiKey: string,
        guid: string,
        token: string,
        identityToken: string
    ): Promise<boolean | any> {
        const response = await this.http.delete<any>(this.apiPathManager.getPath(API_IDS.DEVICE_DELETE), {
            params: {
                apikey: apiKey,
                userName: username,
                deviceGuid: guid,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });
        return response.data;
    }

    async getDevices(
        username: string,
        apiKey: string,
        token: string,
        identityToken: string,
        uid?: string | undefined,
        guid?: string | undefined
    ): Promise<IDeviceData[] | any> {
        const searchParams: {
            deviceUniqueIdentifier?: string;
            deviceGuid?: string;
        } = {};
        if (uid) {
            searchParams['deviceUniqueIdentifier'] = uid;
        }
        if (guid) {
            searchParams['deviceGuid'] = guid;
        }
        const response = await this.http.get<IDeviceData>(this.apiPathManager.getPath(API_IDS.DEVICE_ADD), {
            params: {
                userName: username,
                apikey: apiKey,
                ...searchParams,
            },
            headers: {
                Authorization: token,
                identityToken: identityToken,
            },
        });
        return response.data;
    }
}

export default DeviceRepository;
